const HTTP_URL_PATTERN = /^https?:\/\//i;

export type HotspotMarker = {
  key: string;
  x: number;
  y: number;
};

export type HotspotPrompt = {
  stem: string;
  imageSrc: string | null;
  imageAlt: string;
};

// Parses in an inert document: nothing in the markup runs and no resource is loaded.
const parseHtml = (html: string): Document => new DOMParser().parseFromString(html || '', 'text/html');

export const getPlainText = (html: string): string => parseHtml(html).body.textContent || '';

const getSortedChoiceIds = (choices: Record<string, string>): string[] => {
  return Object.keys(choices || {}).sort((id1, id2) => {
    return getPlainText(choices[id1]).trim().localeCompare(getPlainText(choices[id2]).trim());
  });
};

// Choice ids in alphabetical order of their text. The stored order is usually the correct order, so it is never shown.
export const getAlphabeticalChoiceIds = (choices: Record<string, string>): string[] => getSortedChoiceIds(choices);

// Alphabetical order that is never the correct order of an ORDER question.
export const getOrderChoiceIds = (choices: Record<string, string>, correctResponse: string[]): string[] => {
  const choiceIds = getSortedChoiceIds(choices);
  const isCorrectOrder = choiceIds.length > 1
    && choiceIds.every((choiceId, index) => choiceId === correctResponse?.[index]);
  return isCorrectOrder ? [choiceIds[1], choiceIds[0], ...choiceIds.slice(2)] : choiceIds;
};

export const parseHotspotPrompt = (prompt: string): HotspotPrompt => {
  const { body } = parseHtml(prompt);
  const image = body.querySelector('img');
  const src = image?.getAttribute('src') || '';
  const imageAlt = image?.getAttribute('alt') || '';
  body.querySelectorAll('img').forEach(imageElement => imageElement.remove());
  return {
    stem: (body.textContent || '').trim(),
    imageSrc: HTTP_URL_PATTERN.test(src.trim()) ? src.trim() : null,
    imageAlt,
  };
};

export const getHotspotMarkers = (targetResponseChoices: unknown): HotspotMarker[] => {
  return Object.entries((targetResponseChoices || {}) as Record<string, { x?: unknown; y?: unknown }>)
    .filter(([, target]) => typeof target?.x === 'number' && typeof target?.y === 'number')
    .map(([key, target]) => ({ key, x: target.x as number, y: target.y as number }));
};
